import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_PATHS } from "../../api/apiPaths";
import { http } from "../../api/httpClient";
import { trackCreateBusinessStep } from "../../utils/analytics";
import { getExploreCategoryTone } from "../requirements/CategorySelectionScreen";
import "./createBusinessCategory.css";

const interiorIcon = "https://d1vqw4it8x9uac.cloudfront.net/admin/interior-design-bro-1-1790236892.png";
const architectureIcon = "https://d1vqw4it8x9uac.cloudfront.net/admin/house-restyling-bro-1-1790236892.png";

// Same catch-all category id the app treats specially (see
// ProfessionalOnboardingStart.tsx OTHERS_CATEGORY_CID) — picking it reveals a
// required free-text field so the user can name a category we don't list.
const OTHERS_CATEGORY_CID = "11";

const FALLBACK_BUSINESS_CATEGORIES = [
  { id: "interior_design", apiId: "interior_design", label: "Interior Design", icon: interiorIcon },
  { id: "architecture", apiId: "architecture", label: "Architecture", icon: architectureIcon },
  { id: "furniture", apiId: "furniture", label: "Furniture", icon: interiorIcon },
  { id: "vastu", apiId: "vastu", label: "Vastu", icon: architectureIcon },
  { id: "automation", apiId: "automation", label: "Automation", icon: interiorIcon },
];

/** True when the category carries a real image, not the generic fallback. */
const hasRealBusinessIcon = (icon) => {
  const value = String(icon || "").trim();
  return value !== "" && value !== "/Category.svg";
};

const normalizeCategoryId = (category) => {
  if (category?.name) {
    const normalized = String(category.name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    if (normalized) return normalized;
  }
  return String(category?.cid || category?._id || "");
};

function CreateBusinessCategoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const progress = 50;
  const [categories, setCategories] = useState(FALLBACK_BUSINESS_CATEGORIES);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [otherCategoryName, setOtherCategoryName] = useState("");
  const [otherCategoryError, setOtherCategoryError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const { data: responseData } = await http.get(API_PATHS.CATEGORIES, {
          signal: controller.signal,
        });
        const categoryList = Array.isArray(responseData)
          ? responseData
          : responseData?.data || [];

        const normalizedCategories = categoryList
          .map((category, index) => ({
            id: normalizeCategoryId(category),
            apiId: category?.cid || category?._id || "",
            label: category?.name || "Unnamed category",
            icon:
              category?.icon ||
              FALLBACK_BUSINESS_CATEGORIES[index % FALLBACK_BUSINESS_CATEGORIES.length]?.icon,
          }))
          .filter((category) => category.id && category.label);

        if (normalizedCategories.length > 0) {
          setCategories(normalizedCategories);
          return;
        }

        setCategories(FALLBACK_BUSINESS_CATEGORIES);
      } catch (error) {
        if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") return;
        setCategories(FALLBACK_BUSINESS_CATEGORIES);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();

    return () => controller.abort();
  }, []);

  const filteredCategories = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return categories;
    return categories.filter((item) =>
      item.label.toLowerCase().includes(normalizedSearch),
    );
  }, [categories, searchTerm]);

  const isOthersSelected =
    !!selectedCategory && String(selectedCategory.apiId) === OTHERS_CATEGORY_CID;

  const handleSelectCategory = (item) => {
    setSelectedCategory(item);
    if (String(item.apiId) !== OTHERS_CATEGORY_CID) {
      setOtherCategoryName("");
      setOtherCategoryError("");
    }
  };

  const renderCategoryCard = (item) => {
    const selected = selectedCategory?.id === item.id;
    const tone = getExploreCategoryTone(item.label);
    const CategoryIcon = tone.Icon;
    const showImage = hasRealBusinessIcon(item.icon);
    return (
      <button
        type="button"
        key={item.id}
        className={`create-business-category-card ${selected ? "is-selected" : ""}`}
        onClick={() => handleSelectCategory(item)}
      >
        <span
          className="create-business-category-card-icon-wrap"
          style={{ backgroundColor: tone.bg }}
        >
          {showImage ? (
            <img src={item.icon} alt="" className="create-business-category-card-icon" />
          ) : (
            <CategoryIcon size={24} color={tone.color} weight="fill" />
          )}
        </span>
        <span className="create-business-category-card-text">
          <span className="create-business-category-card-label">{item.label}</span>
          <span className="create-business-category-card-desc">{tone.subtitle}</span>
        </span>
      </button>
    );
  };

  return (
    <div className="create-business-category-page">
      <div className="create-business-category-shell">
        <main className="create-business-category-main">
          <div className="create-business-category-inline-header">
            <button
              type="button"
              className="create-business-category-back-btn-icon"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <img src="/caretLeft.svg" alt="" />
            </button>
            <p>Tell us about you</p>
          </div>

          <h1 className="create-business-category-title">Select your business category.</h1>
          <p className="create-business-category-subtitle">
            Can&apos;t be changed later so choose carefully.
          </p>

          <input
            type="text"
            className="create-business-category-search"
            placeholder="Search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />

          {isLoadingCategories ? (
            <div className="create-business-category-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={`biz-cat-skeleton-${i}`}
                  className="create-business-category-card is-skeleton"
                >
                  <span className="create-business-category-skel-icon" />
                  <span className="create-business-category-skel-text">
                    <span className="create-business-category-skel-line wide" />
                    <span className="create-business-category-skel-line narrow" />
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="create-business-category-grid">
              {filteredCategories.map((item) => renderCategoryCard(item))}
            </div>
          )}

          {isOthersSelected ? (
            <div className="create-business-category-other-block">
              <label htmlFor="other-category-input" className="create-business-category-other-label">
                Enter your business category{" "}
                <span className="create-business-category-required">*</span>
              </label>
              <input
                id="other-category-input"
                type="text"
                className={`create-business-category-other-input ${
                  otherCategoryError ? "has-error" : ""
                }`}
                placeholder="e.g. Glass Work, Metal Fabrication"
                value={otherCategoryName}
                onChange={(event) => {
                  setOtherCategoryName(event.target.value);
                  if (otherCategoryError) setOtherCategoryError("");
                }}
              />
              {otherCategoryError ? (
                <p className="create-business-category-other-error">{otherCategoryError}</p>
              ) : null}
            </div>
          ) : null}
        </main>

        <footer className="create-business-category-footer">
          <div className="create-business-category-progress-track">
            <div
              className="create-business-category-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <button
            type="button"
            className="create-business-category-continue-btn"
            disabled={!selectedCategory}
            onClick={() => {
              const otherServiceName = otherCategoryName.trim();
              if (isOthersSelected && !otherServiceName) {
                setOtherCategoryError("Please enter your business category");
                return;
              }

              trackCreateBusinessStep({
                step: 2,
                buttonName: selectedCategory?.label || "Select category",
              });
              navigate("/create-business/profile", {
                state: {
                  categoryId: selectedCategory?.apiId || selectedCategory?.id || "",
                  categoryName: selectedCategory?.label || "",
                  phoneNumber: location?.state?.phoneNumber || "",
                  isOthersCategory: isOthersSelected,
                  otherServiceName: isOthersSelected ? otherServiceName : "",
                },
              });
            }}
          >
            Continue <span aria-hidden="true">&#8250;</span>
          </button>
        </footer>
      </div>
    </div>
  );
}

export default CreateBusinessCategoryPage;
